import React, { useState } from 'react';

// Helper function to get a date in YYYY-MM-DD format based on local timezone
const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const MilkProductionTracker = ({ records, onRecordSubmit, onEditRecord, isSubmitting, isInactive, animalStatus }) => {
    const [newRecord, setNewRecord] = useState({ date: getLocalDateString(new Date()), morningYield: '', eveningYield: '', fatPercent: '', snfPercent: '', pricePerLiter: '' });
    
    const handleRecordChange = (e) => setNewRecord(prev => ({ ...prev, [e.target.name]: e.target.value }));
    
    const handleSubmit = (e) => {
        e.preventDefault();
        if (isInactive) return; // Extra safety check
        onRecordSubmit(newRecord).then(() => {
            setNewRecord({ date: getLocalDateString(new Date()), morningYield: '', eveningYield: '', fatPercent: '', snfPercent: '', pricePerLiter: '' });
        });
    };

    const ModifiedIndicator = () => <span className="text-xs text-gray-400" title="Modified">(M)</span>;

    return (
        <div>
            {isInactive && (
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6" role="alert">
                    <p className="font-bold">Actions Disabled</p>
                    <p>This animal is marked as "{animalStatus}" and can no longer have new milk records added.</p>
                </div>
            )}
            <form onSubmit={handleSubmit} className={`bg-gray-50 p-4 rounded-lg mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${isInactive ? 'opacity-50' : ''}`}>
                <div><label htmlFor="date" className="block text-sm font-medium text-gray-700">Date</label><input type="date" name="date" value={newRecord.date} onChange={handleRecordChange} className="mt-1 block w-full form-input rounded-md shadow-sm" disabled={isInactive} required /></div>
                <div><label htmlFor="morningYield" className="block text-sm font-medium text-gray-700">Morning Yield (L)</label><input type="number" step="0.1" name="morningYield" value={newRecord.morningYield} onChange={handleRecordChange} className="mt-1 block w-full form-input rounded-md shadow-sm" disabled={isInactive} /></div>
                <div><label htmlFor="eveningYield" className="block text-sm font-medium text-gray-700">Evening Yield (L)</label><input type="number" step="0.1" name="eveningYield" value={newRecord.eveningYield} onChange={handleRecordChange} className="mt-1 block w-full form-input rounded-md shadow-sm" disabled={isInactive} /></div>
                <div><label htmlFor="fatPercent" className="block text-sm font-medium text-gray-700">Fat (%)</label><input type="number" step="0.1" name="fatPercent" value={newRecord.fatPercent} onChange={handleRecordChange} className="mt-1 block w-full form-input rounded-md shadow-sm" disabled={isInactive} /></div>
                <div><label htmlFor="snfPercent" className="block text-sm font-medium text-gray-700">SNF (%)</label><input type="number" step="0.1" name="snfPercent" value={newRecord.snfPercent} onChange={handleRecordChange} className="mt-1 block w-full form-input rounded-md shadow-sm" disabled={isInactive} /></div>
                
                <div>
                    <label htmlFor="pricePerLiter" className="block text-sm font-medium text-gray-700">Price / Liter (₹)</label>
                    <input type="number" step="0.01" name="pricePerLiter" value={newRecord.pricePerLiter} onChange={handleRecordChange} className="mt-1 block w-full form-input rounded-md shadow-sm" disabled={isInactive} required />
                </div>

                <div className="lg:col-span-3 flex items-end">
                    <button type="submit" disabled={isInactive || isSubmitting} className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
                        {isSubmitting ? 'Saving...' : 'Add Record'}
                    </button>
                </div>
            </form>

            <h4 className="text-lg font-medium text-gray-800 mb-2">Recent History</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total (L)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price/L (₹)</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Daily Income (₹)</th>
                            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {records.length === 0 && <tr><td colSpan="5" className="text-center py-4 text-gray-500">No milk records found.</td></tr>}
                        {records.map(rec => { 
                            const total = (rec.morningYield || 0) + (rec.eveningYield || 0);
                            const income = total * (rec.pricePerLiter || 0);
                            return (
                                <tr key={rec.id}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">{new Date(rec.date + 'T00:00:00').toLocaleDateString()}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">{total.toFixed(1)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{(rec.pricePerLiter || 0).toFixed(2)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">{income.toFixed(2)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => onEditRecord(rec)} className="text-indigo-600 hover:text-indigo-900">Edit</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default MilkProductionTracker;